import React, {
  createContext,
  useReducer,
  useMemo,
  useContext,
  useRef,
  useCallback,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { Cue } from 'app-types';

interface PlayerState {
  isContentStarted: boolean;
  isPlaying: boolean;
  position: number;
  duration: number;
  isAdActive: boolean;
  adPosition: number;
  adDuration: number;
}

type PlayerAction =
  | { type: 'CONTENT_STARTED' }
  | { type: 'PLAYING'; payload: boolean }
  | { type: 'AD_BREAK_STARTED' }
  | { type: 'AD_POSITION'; payload: { position: number; duration: number } }
  | { type: 'AD_BREAK_COMPLETED' }
  | { type: 'POSITION'; payload: { position: number; duration?: number } }
  | { type: 'CONTENT_CHANGED' };

interface PlayerRef {
  play: () => void;
  pause: () => void;
  seek: (position: number) => void;
  resetResource: () => void;
  mute: (muted: boolean) => void;
  enterFullscreen: () => void;
  exitFullscreen: () => void;
}

interface PlayerActions {
  setPlayer: (player: PlayerRef | null) => void;
  setContentStarted: () => void;
  setContentChanged: () => void;
  setAdBreakStarted: () => void;
  setAdPosition: (position: number, duration: number) => void;
  setAdBreakCompleted: () => void;
  setIsPlaying: (value: boolean) => void;
  setPosition: (position: number, duration: number) => void;
  play: () => void;
  pause: () => void;
  seek: (seekPos: number) => void;
  setCues: (data?: CuesData | null) => void;
  resetResource: () => void;
  mute: () => void;
  unmute: () => void;
  enterFullscreen: () => void;
  exitFullscreen: () => void;
}

export type CuesData = Record<string, Cue>;

const PlayerStateReadContext = createContext<PlayerState | undefined>(
  undefined,
);
const PlayerStateActionContext = createContext<PlayerActions | undefined>(
  undefined,
);
const PlayerCaptionsContext = createContext<CuesData | undefined>(undefined);

export const usePlayerState = (): PlayerState | undefined =>
  useContext(PlayerStateReadContext);
export const usePlayerAction = (): PlayerActions | undefined =>
  useContext(PlayerStateActionContext);
export const usePlayerCaptions = (): CuesData | undefined =>
  useContext(PlayerCaptionsContext);

const usePlayerRef = (): [
  React.MutableRefObject<PlayerRef | null>,
  (player: PlayerRef | null) => void,
] => {
  const playerRef = useRef<PlayerRef | null>(null);
  const setPlayer = useCallback((player: PlayerRef | null) => {
    playerRef.current = player;
  }, []);

  return [playerRef, setPlayer];
};

interface PlayerStateProviderProps {
  children: ReactNode;
}

export const PlayerStateProvider = ({ children }: PlayerStateProviderProps) => {
  const initialState: PlayerState = {
    isContentStarted: false,
    isPlaying: false,
    position: 0,
    duration: 0,
    isAdActive: false,
    adPosition: 0,
    adDuration: 0,
  };

  const reducer = (prevState: PlayerState, action: PlayerAction): PlayerState => {
    switch (action.type) {
      case 'CONTENT_STARTED':
        return {
          ...prevState,
          isContentStarted: true,
        };
      case 'PLAYING':
        return {
          ...prevState,
          isPlaying: action.payload,
        };
      case 'AD_BREAK_STARTED':
        return {
          ...prevState,
          isPlaying: true,
          isAdActive: true,
        };
      case 'AD_POSITION':
        // Prevent unnecessary re-renders.  Position updates 2-4 times per second
        if (
          prevState.adPosition === action.payload.position &&
          prevState.duration === action.payload.duration
        ) {
          return prevState;
        }

        return {
          ...prevState,
          adPosition: action.payload.position,
          adDuration: action.payload.duration,
        };
      case 'AD_BREAK_COMPLETED':
        return {
          ...prevState,
          isAdActive: false,
          adPosition: 0,
          adDuration: 0,
        };
      case 'POSITION':
        // Prevent unnecessary re-renders.  Position updates 2-4 times per second
        if (
          prevState.position === action.payload.position &&
          prevState.duration === action.payload.duration
        ) {
          return prevState;
        }

        return {
          ...prevState,
          position: action.payload.position,
          // TODO: Pull this out into its own action, possibly from its own event from CVP too
          duration: action.payload.duration || prevState.duration,
        };
      case 'CONTENT_CHANGED':
        return initialState;
      default:
        return prevState;
    }
  };

  const [state, dispatchState] = useReducer(reducer, initialState);
  const [cues, setCuesData] = useState<CuesData>({});
  const [playerRef, setPlayer] = usePlayerRef();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>();

  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, []);

  const actions = useMemo((): PlayerActions => {
    const setContentStarted = () => {
      dispatchState({ type: 'CONTENT_STARTED' });
    };
    const setContentChanged = () => {
      dispatchState({ type: 'CONTENT_CHANGED' });
    };
    const setAdBreakStarted = () => {
      dispatchState({ type: 'AD_BREAK_STARTED' });
    };
    const setAdBreakCompleted = () => {
      dispatchState({ type: 'AD_BREAK_COMPLETED' });
    };
    const setAdPosition = (position: number, duration: number) => {
      dispatchState({
        type: 'AD_POSITION',
        payload: {
          position: Math.floor(position),
          duration: Math.floor(duration),
        },
      });
    };
    const setIsPlaying = (value: boolean) => {
      dispatchState({ type: 'PLAYING', payload: value });
    };
    const setPosition = (position: number, duration: number) => {
      dispatchState({
        type: 'POSITION',
        payload: {
          position: Math.floor(position),
          duration: Math.floor(duration),
        },
      });
    };

    // It takes a noticable amount of milliseconds before the player receives the state update
    // So update state in advance for UI.  Note player still updates state too
    const play = () => {
      if (playerRef.current) {
        dispatchState({ type: 'PLAYING', payload: true });
        playerRef.current.play();
      }
    };

    const pause = () => {
      if (playerRef.current) {
        dispatchState({ type: 'PLAYING', payload: false });
        playerRef.current.pause();
      }
    };

    const seek = (seekPos: number) => {
      if (playerRef.current) {
        dispatchState({ type: 'POSITION', payload: { position: seekPos } });
        playerRef.current.seek(Math.floor(seekPos));
      }
    };

    const resetResource = () => {
      if (playerRef.current) {
        setContentChanged();
        playerRef.current.resetResource();
      }
    };

    const setCues = (data?: CuesData | null) => {
      clearTimeout(timeoutRef.current);
      // TODO: Need to verify that multiline captions are not affected since we are currently only using the first cue to determine the cue duration
      // Unable to test this on local so will need to test on QA once CSVSTV-617 has been merged
      const cueDuration =
        data && data[0] ? ((data[0].endTime as number) - (data[0].startTime as number)) * 1000 : 1000;

      setCuesData(data || {});

      timeoutRef.current = setTimeout(() => {
        setCuesData({});
      }, cueDuration);
    };

    const mute = () => {
      if (playerRef.current) {
        playerRef.current.mute(true);
      }
    };

    const unmute = () => {
      if (playerRef.current) {
        playerRef.current.mute(false);
      }
    };

    const enterFullscreen = () => {
      if (playerRef.current) {
        playerRef.current.enterFullscreen();
      }
    };

    const exitFullscreen = () => {
      if (playerRef.current) {
        playerRef.current.exitFullscreen();
      }
    };

    return {
      setPlayer,
      setContentStarted,
      setContentChanged,
      setAdBreakStarted,
      setAdPosition,
      setAdBreakCompleted,
      setIsPlaying,
      setPosition,
      play,
      pause,
      seek,
      setCues,
      resetResource,
      mute,
      unmute,
      enterFullscreen,
      exitFullscreen,
    };
  }, [playerRef, setPlayer]);

  return (
    <PlayerStateReadContext.Provider value={state}>
      <PlayerStateActionContext.Provider value={actions}>
        <PlayerCaptionsContext.Provider value={cues}>
          {children}
        </PlayerCaptionsContext.Provider>
      </PlayerStateActionContext.Provider>
    </PlayerStateReadContext.Provider>
  );
};
